import { Store } from 'vuex';

import { AppState } from '@/store';
import { isValidPhoneNumber, isValidEmail, any, isValidCustomerName, isFormField } from '@/helpers';
import { FormField, ErrorMode } from '@/models';
import { CheckoutModuleState, OneTimePaymentForm } from '@/store/checkout';
import { OrderService } from '../order/order.service';

export type PickupField = keyof CheckoutModuleState['pickup'];
export type OneTimePaymentField = keyof OneTimePaymentForm;
export type GuestInfoField = keyof CheckoutModuleState['guest']['info'];

export interface CheckoutValidationService {
  validate(isAboveStorePayment: boolean): boolean;
  validatePickup(fields?: PickupField[]): boolean;
  validateGuestInfo(fields?: GuestInfoField[]): boolean;
}

const MAX_CUSTOMER_NAME_LENGTH = 35;

export class AppCheckoutValidationService implements CheckoutValidationService {
  constructor(private store: Store<AppState>, private order: OrderService) {}

  validate(isAboveStorePayment: boolean = false) {
    const customer = this.store.state.customer.profile;

    const isGuest = !customer || customer.isGuest;
    const isPickup = this.order.isPickingUp();

    const validationMethods = [] as (() => boolean)[];

    if (isPickup) {
      validationMethods.push(() => this.validatePickup(undefined, true));
    }

    if (isGuest) {
      validationMethods.push(() => this.validateGuestInfo(undefined, true));
    }

    let isValid = true;
    for (const fn of validationMethods) {
      if (!fn()) {
        isValid = false;
      }
    }

    return isValid;
  }

  validatePickup(fields?: PickupField[], touchFields: boolean = false) {
    const isPickup = this.order.isPickingUp();

    if (!isPickup) {
      return true;
    }

    const pickup = this.store.state.checkout.pickup;
    const { pickupTime } = pickup;

    const allFieldNames = Object.keys(pickup) as PickupField[];
    const allFields = this.getFormFieldsFromForm(allFieldNames, pickup);

    const checkedFieldNames = fields || allFieldNames;
    this.resetFields(checkedFieldNames.map(f => pickup[f]).filter(isFormField));

    if (touchFields) {
      this.touchFields(allFields);
    }

    if (checkedFieldNames.includes('pickupTime') && !pickupTime.value) {
      pickupTime.error = `Please enter a pickup time`;
    }

    return !any(allFields, f => !!f.error);
  }

  validateGuestInfo(fields?: GuestInfoField[], touchFields: boolean = false) {
    const info = this.store.state.checkout.guest.info;
    const { customerName, phoneNumber, email } = info;

    const allFieldNames = Object.keys(info) as GuestInfoField[];
    const allFields = this.getFormFieldsFromForm(allFieldNames, info);

    const checkedFieldNames = fields || allFieldNames;
    this.resetFields(checkedFieldNames.map(f => info[f]));

    if (touchFields) {
      this.touchFields(allFields);
    }

    if (checkedFieldNames.includes('customerName')) {
      if (!customerName.value) {
        customerName.error = 'Your Name is required';
        customerName.errorMode = ErrorMode.PlaceHolder;
      } else if (!isValidCustomerName(customerName.value)) {
        customerName.error = 'No special characters allowed';
        customerName.errorMode = ErrorMode.BelowField;
      }

      if (customerName.value.length > MAX_CUSTOMER_NAME_LENGTH) {
        customerName.error = `Please enter a name with less than ${MAX_CUSTOMER_NAME_LENGTH} characters`;
        customerName.errorMode = ErrorMode.BelowField;
      }
    }

    if (checkedFieldNames.includes('phoneNumber')) {
      if (!phoneNumber.value) {
        phoneNumber.error = 'Phone Number is required';
        phoneNumber.errorMode = ErrorMode.PlaceHolder;
      } else if (!isValidPhoneNumber(phoneNumber.value)) {
        phoneNumber.error = 'Phone number is incomplete';
        phoneNumber.errorMode = ErrorMode.BelowField;
      }
    }

    if (checkedFieldNames.includes('email')) {
      if (!email.value) {
        email.error = 'Email is required';
        email.errorMode = ErrorMode.PlaceHolder;
      } else if (!isValidEmail(email.value)) {
        email.error = 'Looks like an invalid email';
        email.errorMode = ErrorMode.BelowField;
      }
    }

    return !any(allFields, f => !!f.error);
  }

  resetFields(fields: FormField<any>[]) {
    for (const field of fields) {
      field.error = null;
    }
  }

  touchFields(fields: FormField<any>[]) {
    for (const field of fields) {
      field.dirty = true;
    }
  }

  private getFormFieldsFromForm<T>(fieldNames: (keyof T)[], form: T) {
    return fieldNames.map(f => (form as any)[f] as FormField);
  }
}
